import { GP_MODAL_VARIANT_ACKNOWLEDGED } from './GatorProtectAcknowledgedModal/GatorProtectAcknowledgedModal.types';
import { GP_MODAL_VARIANT_DETECTED_THREADS } from './GatorProtectDetectedThreadsModal/GatorProtectDetectedThreadsModal.types';
import { GP_MODAL_VARIANT_NO_THREATS } from './GatorProtectNoThreatsModal/GatorProtectNoThreatsModal.types';
import { GP_MODAL_VARIANT_MIGRATE_TO_GATOR_PROTECT } from './MigrateToGatorProtectModal/MigrateToGatorProtectModal.types';

/**
 * Base order before `reverse()` in `pickGatorProtectModalToShow`.
 * Reversed result is bottom → top (DOM / z-index); last entry is the dismiss “top of stack”.
 */
const GP_MODAL_EXCLUSIVE_ORDER = [
  GP_MODAL_VARIANT_DETECTED_THREADS,
  GP_MODAL_VARIANT_ACKNOWLEDGED,
  GP_MODAL_VARIANT_NO_THREATS,
  GP_MODAL_VARIANT_MIGRATE_TO_GATOR_PROTECT,
] as const;

export type GPModalsVariants = (typeof GP_MODAL_EXCLUSIVE_ORDER)[number];

/** Per variant: `true` may be picked to show; `false` stays out until restore or explicit `SET_MODAL`. */
export type GPAvailableModals = Record<GPModalsVariants, boolean>;

/** Reducer seed: no modal variant is available to pick yet. */
export const clearedModalsAvailability: GPAvailableModals = {
  [GP_MODAL_VARIANT_MIGRATE_TO_GATOR_PROTECT]: false,
  [GP_MODAL_VARIANT_NO_THREATS]: false,
  [GP_MODAL_VARIANT_ACKNOWLEDGED]: false,
  [GP_MODAL_VARIANT_DETECTED_THREADS]: false,
};

/** `RESTORE_ALL_MODALS` target: every variant may be offered again (if business rules allow). */
const restoredModalsAvailability: GPAvailableModals = {
  [GP_MODAL_VARIANT_MIGRATE_TO_GATOR_PROTECT]: true,
  [GP_MODAL_VARIANT_NO_THREATS]: true,
  [GP_MODAL_VARIANT_ACKNOWLEDGED]: true,
  [GP_MODAL_VARIANT_DETECTED_THREADS]: true,
};

type GPModalVisibilityAction =
  | { type: 'SET_MODAL'; modal: GPModalsVariants; visible: boolean }
  | { type: 'RESTORE_ALL_MODALS' };

export function gpModalControllerReducer(
  state: GPAvailableModals,
  action: GPModalVisibilityAction,
): GPAvailableModals {
  switch (action.type) {
    case 'SET_MODAL':
      return { ...state, [action.modal]: action.visible };
    case 'RESTORE_ALL_MODALS':
      return restoredModalsAvailability;
    default:
      return state;
  }
}

/**
 * Returns every modal variant that should render when its slot is available (`true`),
 * ordered bottom-to-top (later items paint above earlier ones).
 */
export function pickGatorProtectModalToShow(
  availableModals: GPAvailableModals,
): GPModalsVariants[] {
  return [...GP_MODAL_EXCLUSIVE_ORDER].reverse().filter(variant => availableModals[variant]);
}
